import { NOTHING_MATCHED_OUTCOME } from './watchlistImportOutcome';

export const OPERATION_PHASE_STATUS_QUEUED = 'queued';
export const OPERATION_PHASE_STATUS_RUNNING = 'running';
export const OPERATION_PHASE_STATUS_SUCCESS = 'success';
export const OPERATION_PHASE_STATUS_PARTIAL = 'partial';
export const OPERATION_PHASE_STATUS_FAILED = 'failed';
export const OPERATION_PHASE_STATUS_SKIPPED = 'skipped';

export const OVERALL_STATUS_RUNNING = 'running';
export const OVERALL_STATUS_SUCCESS = 'success';
export const OVERALL_STATUS_PARTIAL = 'partial';
export const OVERALL_STATUS_FAILED = 'failed';
export const OVERALL_STATUS_UNKNOWN = 'unknown';

export const SECURITY_OPERATION_READ_STATES = Object.freeze({
    INACTIVE: 'inactive',
    LOADING: 'loading',
    FOUND: 'found',
    NOT_FOUND: 'not_found',
    DISABLED: 'disabled',
    UNAVAILABLE: 'unavailable',
});

export const DEFAULT_STATUS_RETRY_BASE_MS = 30 * 1000;
export const DEFAULT_STATUS_RETRY_MAX_MS = 2 * 60 * 1000;

export function computeStatusRetryDelay({
    failureCount,
    baseMs = DEFAULT_STATUS_RETRY_BASE_MS,
    maxMs = DEFAULT_STATUS_RETRY_MAX_MS,
    jitter = Math.random,
} = {}) {
    const normalizedFailureCount = Math.max(1, Math.floor(Number(failureCount) || 1));
    const normalizedBaseMs = Math.max(500, Number(baseMs) || DEFAULT_STATUS_RETRY_BASE_MS);
    const normalizedMaxMs = Math.max(normalizedBaseMs, Number(maxMs) || DEFAULT_STATUS_RETRY_MAX_MS);
    const exponentialMs = Math.min(
        normalizedMaxMs,
        normalizedBaseMs * (2 ** Math.min(20, normalizedFailureCount - 1)),
    );
    const randomValue = Math.max(0, Math.min(1, Number(jitter?.()) || 0));
    const jitteredMs = exponentialMs * (0.75 + (randomValue * 0.5));
    return Math.max(500, Math.min(normalizedMaxMs, Math.round(jitteredMs)));
}

const TERMINAL_OVERALL_STATUSES = new Set([
    OVERALL_STATUS_SUCCESS,
    OVERALL_STATUS_PARTIAL,
    OVERALL_STATUS_FAILED,
]);

export const DEFAULT_ACTIVE_WINDOW_MS = 10 * 60 * 1000;
export const DEFAULT_SETTLE_WINDOW_MS = 30 * 1000;
export const DEFAULT_SUCCESS_SETTLE_WINDOW_MS = 3 * 1000;

// One completion-window policy for status rendering and the hook's timer.
export function securityOperationSettleWindowMs(overallStatus, options = {}) {
    if (typeof options.settleWindowMs === 'number') return options.settleWindowMs;
    return overallStatus === OVERALL_STATUS_SUCCESS
        ? DEFAULT_SUCCESS_SETTLE_WINDOW_MS : DEFAULT_SETTLE_WINDOW_MS;
}

const TERMINAL_PHASE_STATUSES = new Set([
    OPERATION_PHASE_STATUS_SUCCESS,
    OPERATION_PHASE_STATUS_PARTIAL,
    OPERATION_PHASE_STATUS_FAILED,
    OPERATION_PHASE_STATUS_SKIPPED,
]);

const FACTOR_MATRIX_PREREQUISITE_PHASES = [
    'mutation',
    'identity_resolution',
    'market_registration',
];

function normalizePhaseStatus(raw) {
    const value = String(raw == null ? '' : raw).trim().toLowerCase();
    switch (value) {
        case OPERATION_PHASE_STATUS_QUEUED:
        case OPERATION_PHASE_STATUS_RUNNING:
        case OPERATION_PHASE_STATUS_SUCCESS:
        case OPERATION_PHASE_STATUS_PARTIAL:
        case OPERATION_PHASE_STATUS_FAILED:
        case OPERATION_PHASE_STATUS_SKIPPED:
            return value;
        default:
            return null;
    }
}

function ensureNormalized(statusDoc) {
    if (!statusDoc || typeof statusDoc !== 'object') return null;
    if (
        Object.prototype.hasOwnProperty.call(statusDoc, 'enabled')
        && Object.prototype.hasOwnProperty.call(statusDoc, 'phases')
        && Object.prototype.hasOwnProperty.call(statusDoc, 'topLevelStatus')
    ) {
        return statusDoc;
    }
    return null;
}

export function deriveSecurityOperationOverallStatus(phases, topLevelStatus) {
    const normalizedTopLevelStatus = normalizePhaseStatus(topLevelStatus);
    if (normalizedTopLevelStatus) return normalizedTopLevelStatus;

    const phaseStatuses = Object.values(phases || {})
        .map((phase) => normalizePhaseStatus(phase?.status))
        .filter(Boolean);
    if (phaseStatuses.length === 0) return OVERALL_STATUS_UNKNOWN;
    if (phaseStatuses.some((status) => status === OPERATION_PHASE_STATUS_QUEUED || status === OPERATION_PHASE_STATUS_RUNNING)) {
        return OVERALL_STATUS_RUNNING;
    }
    if (phaseStatuses.includes(OPERATION_PHASE_STATUS_FAILED)) {
        return phaseStatuses.some((status) => status === OPERATION_PHASE_STATUS_SUCCESS || status === OPERATION_PHASE_STATUS_PARTIAL)
            ? OVERALL_STATUS_PARTIAL
            : OVERALL_STATUS_FAILED;
    }
    if (phaseStatuses.includes(OPERATION_PHASE_STATUS_PARTIAL)) return OVERALL_STATUS_PARTIAL;
    if (phaseStatuses.every((status) => TERMINAL_PHASE_STATUSES.has(status))) {
        return OVERALL_STATUS_SUCCESS;
    }
    return OVERALL_STATUS_UNKNOWN;
}

export function deriveSecurityOperationProgress(statusDoc) {
    const normalized = ensureNormalized(statusDoc);
    if (!normalized || !normalized.found) return null;
    if (typeof normalized.progressPercent === 'number') return normalized.progressPercent;
    if (normalized.indeterminate) return null;
    const overall = deriveSecurityOperationOverallStatus(
        normalized.phases,
        normalized.topLevelStatus,
    );
    return TERMINAL_OVERALL_STATUSES.has(overall) ? 100 : null;
}

export function isSecurityOperationTerminal(overallStatus) {
    return TERMINAL_OVERALL_STATUSES.has(String(overallStatus || '').trim().toLowerCase());
}

/**
 * The one notice a finished Watchlist update earns: a failure, or an import
 * that matched nothing (a plain outcome, shown as information).
 */
export function watchlistUpdateOutcomeNotice(statusDoc) {
    const normalized = ensureNormalized(statusDoc);
    if (!normalized?.found || normalized.operationType !== 'watchlist_update') {
        return null;
    }
    if (normalized.outcome === NOTHING_MATCHED_OUTCOME) {
        return {
            key: normalized.revisionId || normalized.operationId || 'watchlist-update-nothing-matched',
            message: normalized.message,
            severity: 'info',
        };
    }
    const overall = deriveSecurityOperationOverallStatus(
        normalized.phases,
        normalized.topLevelStatus,
    );
    if (overall !== OVERALL_STATUS_FAILED) return null;
    return {
        key: normalized.revisionId || normalized.operationId || 'watchlist-update-failed',
        message: 'Watchlist update failed. Your active Watchlist was not changed.',
        severity: 'error',
    };
}

export function areFactorMatrixPrerequisitesTerminal(statusDoc) {
    const normalized = ensureNormalized(statusDoc);
    if (!normalized?.enabled || !normalized?.found || !normalized?.operationId) {
        return false;
    }
    const requiredPhases = new Set(normalized.requiredPhases || []);
    const prerequisites = FACTOR_MATRIX_PREREQUISITE_PHASES.filter((phase) => (
        requiredPhases.has(phase)
    ));
    if (prerequisites.length === 0) {
        return false;
    }
    return prerequisites.every((phase) => (
        TERMINAL_PHASE_STATUSES.has(normalized.phases?.[phase]?.status)
    ));
}

export function isSecurityOperationActive(statusDoc, options = {}) {
    const normalized = ensureNormalized(statusDoc);
    if (!normalized || !normalized.found || !normalized.enabled) return false;
    const overall = deriveSecurityOperationOverallStatus(
        normalized.phases,
        normalized.topLevelStatus,
    );
    if (!isSecurityOperationTerminal(overall)) return true;
    return isSecurityOperationWithinSettleWindow(normalized, options);
}

export function isSecurityOperationWithinSettleWindow(statusDoc, options = {}) {
    const normalized = ensureNormalized(statusDoc);
    if (!normalized || !normalized.updatedAt) return false;
    const overall = deriveSecurityOperationOverallStatus(
        normalized.phases,
        normalized.topLevelStatus,
    );
    if (!isSecurityOperationTerminal(overall)) return false;
    const nowMs = typeof options.nowMs === 'number' ? options.nowMs : Date.now();
    const settleWindowMs = securityOperationSettleWindowMs(overall, options);
    const ageMs = nowMs - normalized.updatedAt.getTime();
    // Match the success timer's deadline so a poll at exactly three seconds
    // cannot flash a completed bar again. Other terminal windows stay unchanged.
    return overall === OVERALL_STATUS_SUCCESS ? ageMs < settleWindowMs : ageMs <= settleWindowMs;
}

export function shouldShowSecurityOperationBanner(statusDoc, options = {}) {
    const normalized = ensureNormalized(statusDoc);
    if (!normalized || !normalized.found || !normalized.enabled) return false;
    const nowMs = typeof options.nowMs === 'number' ? options.nowMs : Date.now();
    if (normalized.expiresAt && normalized.expiresAt.getTime() <= nowMs) return false;
    const overall = deriveSecurityOperationOverallStatus(
        normalized.phases,
        normalized.topLevelStatus,
    );
    if (!isSecurityOperationTerminal(overall)) {
        const updatedAtMs = normalized.updatedAt?.getTime();
        if (!updatedAtMs) return true;
        const activeWindowMs = typeof options.activeWindowMs === 'number'
            ? options.activeWindowMs
            : DEFAULT_ACTIVE_WINDOW_MS;
        return nowMs - updatedAtMs <= activeWindowMs;
    }
    return isSecurityOperationWithinSettleWindow(normalized, options);
}
